import BaseApi, { Response, PagedResponse } from './baseApi';
import {
  BaseAssessmentItemExchange,
  CreateAssessmentItemExchange,
  UpdateAssessmentItemExchange,
} from '../dtos/AssessmentItemExchanges/index';

class AssessmentItemExchangeApi extends BaseApi {
  private readonly basePath = '/assessment-item-exchanges';

  /**
   * Get assessment item exchange by ID
   * GET /api/assessment-item-exchanges/{id}
   */
  async getById(id: string): Promise<Response<BaseAssessmentItemExchange | null>> {
    return this.get<BaseAssessmentItemExchange | null>(`${this.basePath}/${id}`);
  }

  /**
   * Get all assessment item exchanges
   * GET /api/assessment-item-exchanges
   */
  async getList(): Promise<Response<BaseAssessmentItemExchange[]>> {
    return this.get<BaseAssessmentItemExchange[]>(this.basePath);
  }

  /**
   * Get assessment item exchanges by assessment exchange ID
   * GET /api/assessment-item-exchanges/assessment-exchange/{assessmentExchangeId}
   */
  async getByAssessmentExchangeId(assessmentExchangeId: string): Promise<Response<BaseAssessmentItemExchange[]>> {
    return this.get<BaseAssessmentItemExchange[]>(`${this.basePath}/assessment-exchange/${assessmentExchangeId}`);
  }

  /**
   * Get paginated list of assessment item exchanges with optional search
   * GET /api/assessment-item-exchanges/search?searchTerm=&pageNumber=1&pageSize=10 (when search is provided)
   * GET /api/assessment-item-exchanges/paged?pageNumber=1&pageSize=10 (when no search)
   * Searches: AssessmentExchangeId, AssessmentItemId (as strings)
   */
  async getPagedList(
    pageNumber: number = 1,
    pageSize: number = 10,
    searchTerm?: string
  ): Promise<Response<PagedResponse<BaseAssessmentItemExchange>>> {
    if (searchTerm) {
      const params = new URLSearchParams();
      params.append('searchTerm', searchTerm);
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      return this.get<PagedResponse<BaseAssessmentItemExchange>>(
        `${this.basePath}/search?${params.toString()}`
      );
    }
    
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    return this.get<PagedResponse<BaseAssessmentItemExchange>>(
      `${this.basePath}/paged?${params.toString()}`
    );
  }

  /**
   * Create a new assessment item exchange
   * POST /api/assessment-item-exchanges
   */
  async create(data: CreateAssessmentItemExchange): Promise<Response<BaseAssessmentItemExchange>> {
    return this.post<BaseAssessmentItemExchange, CreateAssessmentItemExchange>(this.basePath, data);
  }

  /**
   * Update an existing assessment item exchange
   * PUT /api/assessment-item-exchanges/{id}
   */
  async update(id: string, data: Omit<UpdateAssessmentItemExchange, 'id'>): Promise<Response<BaseAssessmentItemExchange>> {
    return this.put<BaseAssessmentItemExchange, UpdateAssessmentItemExchange>(`${this.basePath}/${id}`, { ...data, id });
  }

  /**
   * Delete an assessment item exchange
   * DELETE /api/assessment-item-exchanges/{id}
   */
  async deleteAssessmentItemExchange(id: string): Promise<Response<boolean>> {
    return super.delete<boolean>(`${this.basePath}/${id}`);
  }
}

// Export singleton instance
export const assessmentItemExchangeApi = new AssessmentItemExchangeApi();

export default AssessmentItemExchangeApi;
